import type { LegalDoc } from './legalContentTypes';

export const privacyPolicyEn: LegalDoc = {
  title: 'Privacy Policy for Portcierge',
  lastUpdated: 'September 16, 2026',
  sections: [
    {
      heading: 'Introduction',
      blocks: [
        {
          type: 'p',
          text:
            'Welcome to Portcierge ("we," "our," or "us"). We respect your privacy and are committed to protecting your information. This Privacy Policy explains how we collect, use, and safeguard information when you use the Portcierge mobile application (the "App").',
        },
      ],
    },
    {
      heading: 'Information We Collect',
      blocks: [
        {
          type: 'p',
          text: 'The App is designed to be local-first. Most data you create stays on your device unless a feature explicitly requires a network call.',
        },
        {
          type: 'ul',
          items: [
            {
              label: 'Trip and itinerary data',
              text:
                'Trip names, city visits, travel dates, day plans, scheduled items (meals/activities/transport/lodging), notes, options/checklists, and related metadata you create in the App.',
            },
            {
              label: 'Shared Items content (if you use Share into the App)',
              text:
                'Content you share via the iOS Share Sheet (such as URLs, text, PDFs, images, and files) that is stored locally so you can review and save it into a trip.',
            },
            {
              label: 'Location and coordinates',
              text:
                'City coordinates (latitude/longitude) are used to fetch weather forecasts. Live device location is collected only if you explicitly enable optional location sharing features (if available).',
            },
            {
              label: 'Preferences',
              text:
                'Display and planning preferences such as temperature units and other app settings.',
            },
            {
              label: 'Device and app information',
              text:
                'Basic device/app details (like app version and OS version) may be processed for troubleshooting and compatibility.',
            },
          ],
        },
      ],
    },
    {
      heading: 'Third-Party Services and Network Calls',
      blocks: [
        {
          type: 'p',
          text:
            'Portcierge uses external services only to power specific features. Depending on what you use and enable, the App may share limited information with:',
        },
        {
          type: 'ul',
          items: [
            {
              label: 'Open-Meteo (weather)',
              text:
                'City coordinates are sent to retrieve daily and hourly forecasts. We do not send your full itinerary to fetch weather.',
            },
            {
              label: 'OpenAI (AI features, if enabled)',
              text:
                'If you choose to use AI features, the App may send relevant text and metadata (for example, city names, dates, and content from Shared Items you request to process) to generate a city plan or extract structured trip details. You review results before saving them into your trip.',
            },
            {
              label: 'AWS Amplify / GraphQL API (community features, if enabled)',
              text:
                'If you enable community features (such as connections/invites and optional location sharing), the App may transmit account identifiers and, if you turn on location sharing, your device location to provide that functionality.',
            },
            {
              label: 'PostHog (analytics and reliability)',
              text:
                'The web app uses PostHog for high-level product events, error tracking, and privacy-masked session replay. We associate signed-in activity with an account identifier, but do not send your email or name as analytics profile properties. Input values, rendered text, and element attributes are masked in replay.',
            },
          ],
        },
      ],
    },
    {
      heading: 'How We Use Your Information',
      blocks: [
        {
          type: 'ul',
          items: [
            { label: 'Provide core app functionality', text: 'Create, store, and display your trips and itineraries.' },
            { label: 'Provide weather services', text: 'Fetch and cache forecasts for your planned destinations.' },
            {
              label: 'Provide optional AI features',
              text: 'Generate city plans and extract structured trip data from items you choose to process.',
            },
            {
              label: 'Provide optional community features',
              text: 'Support invites/connections and optional location sharing when enabled.',
            },
            {
              label: 'Improve reliability',
              text: 'Troubleshoot crashes/bugs and ensure compatibility across devices and OS versions.',
            },
          ],
        },
      ],
    },
    {
      heading: 'Analytics and Telemetry',
      blocks: [
        {
          type: 'p',
          text:
            'If analytics are enabled in the App, we collect high-level feature events (for example, trip creation or AI plan generation), privacy-masked interaction replays, and technical errors. We avoid collecting sensitive trip contents and do not sell your data.',
        },
      ],
    },
    {
      heading: 'Data Storage, Retention, and Deletion',
      blocks: [
        {
          type: 'ul',
          items: [
            { label: 'Local-first storage', text: 'Trip data is stored on your device by default.' },
            {
              label: 'Share Extension storage',
              text:
                'Shared Items may be stored locally in an app group container so the main app can import and display them.',
            },
            {
              label: 'Cloud data (only if you enable community)',
              text:
                'If you enable community features, related account/community data may be stored and processed by cloud services required to provide those features.',
            },
            {
              label: 'Deletion',
              text:
                'You can delete trips within the App, and uninstalling the App removes local data from your device (subject to your device/backup settings).',
            },
          ],
        },
      ],
    },
    {
      heading: 'Data Sharing and Disclosure',
      blocks: [
        {
          type: 'p',
          text:
            'We do not sell your personal information. We may share limited information only with service providers as needed to deliver features you use (weather, AI, and optional community/location sharing). We may also disclose information if required by law.',
        },
      ],
    },
    {
      heading: 'Children’s Privacy',
      blocks: [
        {
          type: 'p',
          text:
            'The App is not intended for children under 13. We do not knowingly collect personal information from children under 13.',
        },
      ],
    },
    {
      heading: 'International Users',
      blocks: [
        {
          type: 'p',
          text:
            'Portcierge is designed for worldwide use. When network features are used, information may be processed by third-party services in different regions depending on their infrastructure.',
        },
      ],
    },
    {
      heading: 'Changes to This Privacy Policy',
      blocks: [
        {
          type: 'p',
          text:
            'We may update this Privacy Policy from time to time. We will update the "Last Updated" date and may also provide notice within the App.',
        },
      ],
    },
    {
      heading: 'Contact Information',
      blocks: [
        {
          type: 'p',
          text:
            'If you have questions about this Privacy Policy or our data practices, contact us at privacy@portcierge.com.',
        },
      ],
    },
  ],
  footerNote:
    'This Privacy Policy is effective as of the date listed above and applies to all users of the Portcierge app.',
};

